import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { AuthContext } from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
    ArrowLeftIcon, 
    ArrowRightIcon, 
    CheckCircleIcon, 
    BookOpenIcon,
    TrophyIcon,
    ClockIcon
} from '@heroicons/react/24/outline';

const ReadingPage = () => {
    const { id: courseId, pageNumber: pageNumStr } = useParams();
    const pageNumber = parseInt(pageNumStr, 10);
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);

    const [course, setCourse] = useState(null);
    const [enrollment, setEnrollment] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [readingTime, setReadingTime] = useState(0);

    const fetchCourseData = useCallback(async () => {
        if (!token || !courseId || isNaN(pageNumber)) {
             navigate('/login');
             return;
        };
        setLoading(true);
        try {
            const config = { headers: { 'x-auth-token': token } };
            const courseRes = await axios.get(`${API_BASE_URL}/api/courses/${courseId}`, config);
            const fetchedCourse = courseRes.data;
            setCourse(fetchedCourse);

            const enrollmentRes = await axios.get(`${API_BASE_URL}/api/enrollments/status/${courseId}`, config);
            const fetchedEnrollment = enrollmentRes.data;
            setEnrollment(fetchedEnrollment);

            const foundPage = fetchedCourse.readingPages?.find(p => p.pageNumber === pageNumber);
            if (foundPage) {
                setCurrentPage(foundPage);
                setIsCompleted(fetchedEnrollment?.completedPages?.includes(pageNumber) || false);
            } else {
                 toast.error("Invalid page number.");
                 navigate(`/courses/${courseId}`);
            }

        } catch (err) {
            console.error("Course/Enrollment data error:", err);
            if (err.response && err.response.status === 404) {
                 toast.error("You must be enrolled to access this course.");
                 navigate(`/courses/${courseId}`);
            } else {
                 toast.error("Course content could not be loaded.");
                 navigate(`/courses/${courseId}`);
            }
        } finally {
            setLoading(false);
        }
    }, [courseId, pageNumber, token, navigate]);

    useEffect(() => {
        fetchCourseData();
    }, [fetchCourseData]);

    // Reading time tracker
    useEffect(() => {
        const timer = setInterval(() => {
            setReadingTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const markPageComplete = async () => {
        if (isCompleted || !token || !enrollment) return;
        try {
            const config = { headers: { 'x-auth-token': token } };
            const body = { courseId, pageNumber };
            const res = await axios.post(`${API_BASE_URL}/api/enrollments/progress`, body, config);
            setEnrollment(res.data);
            setIsCompleted(true);
            toast.success(`Page ${pageNumber} completed! 🎉`);
        } catch (err) {
            toast.error("Page could not be marked as complete.");
            console.error("Page completion error:", err);
        }
    };

    const goToNextPage = async () => {
        if (!isCompleted) {
             await markPageComplete();
        }

        const totalPages = course?.readingPages?.length || 0;
        if (pageNumber < totalPages) {
            navigate(`/courses/${courseId}/read/${pageNumber + 1}`);
        } else {
            if (course?.quizId) {
                navigate(`/quiz/${course.quizId}`);
            } else {
                toast.success("Congratulations, you have completed the course!");
                navigate(`/courses/${courseId}`);
            }
        }
    };

    const goToPrevPage = () => {
        if (pageNumber > 1) {
            navigate(`/courses/${courseId}/read/${pageNumber - 1}`);
        } else {
            navigate(`/courses/${courseId}`);
        }
    };

    if (loading) return <Spinner />;
    if (!course || !enrollment || !currentPage) return (
        <div className="text-center card p-8 max-w-2xl mx-auto">
            <BookOpenIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>An error occurred while loading the content or you do not have permission to access this page.</p>
        </div>
    );

    const totalPages = course.readingPages?.length || 0;
    const progressPercentage = (enrollment?.completedPages?.length || 0) / totalPages * 100;

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            {/* Top Progress Bar */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }} 
                animate={{ opacity: 1, y: 0 }}
                className="card p-4 sticky top-4 z-10 shadow-xl"
            >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-3">
                    <div className="flex items-center gap-3 flex-1">
                        <Link 
                            to={`/courses/${courseId}`} 
                            className="btn-secondary !py-2 !px-3 text-sm flex items-center gap-1"
                        >
                            <ArrowLeftIcon className="h-4 w-4"/>
                            <span className="hidden sm:inline">Course</span>
                        </Link>
                        <div>
                            <h2 className="font-bold text-lg line-clamp-1">{course.title}</h2>
                            <p className="text-xs opacity-70">
                                {currentPage.title || `Chapter ${pageNumber}`}
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5 bg-light-bg dark:bg-dark-bg px-3 py-1.5 rounded-lg">
                            <ClockIcon className="h-4 w-4" />
                            {formatTime(readingTime)}
                        </div>
                        <span className="font-semibold">{pageNumber} / {totalPages}</span>
                    </div>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-2 overflow-hidden">
                    <motion.div 
                        className="bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 h-2"
                        initial={{ width: `${progressPercentage}%` }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
            </motion.div>

            {/* Main Content Card */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card"
            >
                <div className="p-6 md:p-10">
                    {/* Page Title */}
                    <div className="mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-light-primary/10 dark:bg-dark-primary/10 flex items-center justify-center">
                                <span className="font-bold text-light-primary dark:text-dark-primary">
                                    {pageNumber}
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-bold flex-1">
                                {currentPage.title || `Page ${pageNumber}`}
                            </h1>
                        </div>
                        
                        {isCompleted && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-2 text-green-600 dark:text-green-400 bg-green-500/10 px-4 py-2 rounded-lg"
                            >
                                <CheckCircleIcon className="h-5 w-5"/>
                                <span className="text-sm font-medium">✓ Completed</span>
                            </motion.div>
                        )}
                    </div>

                    {/* Content */}
                    <div
                        className="prose prose-lg dark:prose-invert max-w-none 
                                   prose-headings:font-bold prose-headings:text-light-text dark:prose-headings:text-dark-text
                                   prose-p:text-light-text/90 dark:prose-p:text-dark-text/90
                                   prose-a:text-light-primary dark:prose-a:text-dark-primary prose-a:no-underline hover:prose-a:underline
                                   prose-strong:text-light-text dark:prose-strong:text-dark-text
                                   prose-code:text-light-primary dark:prose-code:text-dark-primary prose-code:bg-light-bg dark:prose-code:bg-dark-bg prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                                   prose-pre:bg-light-bg dark:prose-pre:bg-dark-bg prose-pre:border prose-pre:border-light-border dark:prose-pre:border-dark-border
                                   prose-img:rounded-xl prose-img:shadow-lg"
                        dangerouslySetInnerHTML={{ __html: currentPage.content }}
                    />

                    {/* Mark as Complete Button */}
                    {!isCompleted && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1 }}
                            className="mt-8 p-6 bg-light-bg dark:bg-dark-bg rounded-xl text-center"
                        >
                            <p className="text-sm opacity-70 mb-4">Have you finished reading this page?</p>
                            <button 
                                onClick={markPageComplete}
                                className="btn-primary"
                            >
                                <CheckCircleIcon className="h-5 w-5 inline mr-2"/>
                                Mark as Complete
                            </button>
                        </motion.div>
                    )}
                </div>

                {/* Bottom Navigation */}
                <div className="px-6 md:px-10 py-6 bg-light-bg dark:bg-dark-bg border-t border-light-border dark:border-dark-border flex justify-between items-center gap-4">
                    <button 
                        onClick={goToPrevPage} 
                        className="btn-secondary flex items-center gap-2" 
                        disabled={pageNumber <= 1}
                    >
                        <ArrowLeftIcon className="h-5 w-5"/> 
                        <span className="hidden sm:inline">Previous</span>
                    </button>

                    {/* Page Indicator */}
                    <div className="flex gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                            <Link
                                key={num}
                                to={`/courses/${courseId}/read/${num}`}
                                className={`w-2 h-2 rounded-full transition-all ${
                                    num === pageNumber 
                                        ? 'bg-light-primary dark:bg-dark-primary w-8' 
                                        : enrollment?.completedPages?.includes(num)
                                            ? 'bg-green-500'
                                            : 'bg-light-border dark:bg-dark-border'
                                }`}
                            />
                        ))}
                    </div>

                    <button 
                        onClick={goToNextPage} 
                        className={`btn flex items-center gap-2 ${
                            pageNumber === totalPages && course.quizId 
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600' 
                                : 'btn-primary'
                        }`}
                    >
                        <span className="hidden sm:inline">
                            {pageNumber < totalPages 
                                ? "Next" 
                                : (course.quizId ? "Go to Quiz" : "Finish Course")}
                        </span>
                        {pageNumber === totalPages && course.quizId ? (
                            <TrophyIcon className="h-5 w-5"/>
                        ) : (
                            <ArrowRightIcon className="h-5 w-5"/>
                        )}
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default ReadingPage;